import { parseWebsiteCanvasDraftBrowseGrant } from '../lib/website-templates/canvas-protocol';
import { isWebsiteHexColor } from '../lib/website-templates/contrast';
import { isWebsiteDraftBrowseToken } from '../lib/website-templates/draft-browse';
import {
  WEBSITE_SLOT_ID_MAX_LENGTH,
  WEBSITE_SLOT_ID_RE,
  WEBSITE_TEMPLATE_ASSET_KEY_RE,
  WEBSITE_UUID_RE,
  isOneOf,
  isRecord,
} from '../lib/website-templates/internal';
import { parseWebsiteTemplateManifest } from '../lib/website-templates/manifest';
import { isWebsitePageKind } from '../lib/website-templates/page-kinds';
import { parsePublicStoreWebsiteDto, websiteMediaUrl } from '../lib/website-templates/public-dto';
import { WEBSITE_TEMPLATE_ASSET_REF_PREFIX } from '../lib/website-templates/resolve';
import {
  WEBSITE_SLOT_BATCH_MAX,
  parseStoreWebsiteDocument,
  type WebsiteSlotBatch,
} from '../lib/website-templates/slots';
import { deriveTemplatePublicStatus } from '../lib/website-templates/entry';
import type { SupabaseClient } from '../supabase/client';
import type {
  PublishV3Result,
  PublicStoreWebsiteDto,
  SaveSlotsV3Result,
  StoreWebsiteConversionReport,
  StoreWebsiteEditorState,
  StoreWebsiteEditorStateV3,
  StoreWebsiteEditorTemplate,
  StoreWebsiteTemplateHead,
  TemplatePublicStatus,
  UpdateTemplateVersionV3Result,
  StoreWebsiteRevisionPage,
  StoreWebsiteRevisionSummary,
  WebsiteCanvasRenderData,
  WebsiteDraftMutationResult,
  WebsiteInterfaceLocale,
  WebsiteNotCarriedSlot,
  WebsitePageKind,
  WebsitePublishResult,
  WebsiteReadinessIssue,
  WebsiteReadinessReport,
  WebsiteSlotChrome,
  WebsiteSlotValue,
  WebsiteStageResult,
  WebsiteTemplateCatalogItem,
  WebsiteTemplateCatalogItemV3,
  WebsiteTemplateCatalogPage,
  WebsiteTemplateCatalogPageV3,
  WebsiteTemplateCategory,
} from '../types/website-templates';
import {
  TEMPLATE_PUBLIC_STATUSES,
  WEBSITE_CLIENT_CONTRACT,
  WEBSITE_INTERFACE_LOCALES,
  WEBSITE_ISSUE_CODES,
  WEBSITE_READINESS_SEVERITIES,
  WEBSITE_SLOT_SEMANTICS,
  WEBSITE_TEMPLATE_CATEGORIES,
  WEBSITE_TEMPLATE_KINDS,
} from '../types/website-templates';
import { WebsiteTemplateServiceError, callWebsiteRpc } from './website-templates-errors';

export const WEBSITE_CATALOG_PAGE_LIMIT = 24;

const I18N_KEY_RE = /^admin\.websiteTemplates\.[A-Za-z0-9.-]{1,120}$/;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const CURSOR_RE = /^[A-Za-z0-9_-]{1,200}$/;
const REVISION_SOURCES = [
  'stage',
  'slot',
  'theme',
  'locale',
  'restore',
  'legacy-conversion',
] as const;
const REVISION_STATUSES = ['draft', 'published', 'archived'] as const;

function invalid(): never {
  throw new WebsiteTemplateServiceError('unknown');
}

function uuid(value: unknown): string {
  return typeof value === 'string' && WEBSITE_UUID_RE.test(value) ? value : invalid();
}

function text(value: unknown): string {
  return typeof value === 'string' && value.length > 0 ? value : invalid();
}

function i18nKey(value: unknown): string {
  return typeof value === 'string' && I18N_KEY_RE.test(value) ? value : invalid();
}

function cursor(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' && CURSOR_RE.test(value) ? value : invalid();
}

function slotId(value: unknown): string {
  return typeof value === 'string' &&
    value.length <= WEBSITE_SLOT_ID_MAX_LENGTH &&
    WEBSITE_SLOT_ID_RE.test(value)
    ? value
    : invalid();
}

function ordinal(value: unknown): { readonly ordinal?: number } {
  if (value === undefined || value === null) return {};
  return Number.isInteger(value) && (value as number) >= 1 && (value as number) <= 12
    ? { ordinal: value as number }
    : invalid();
}

function draftResult(data: unknown): WebsiteDraftMutationResult {
  if (!isRecord(data)) return invalid();
  return { draftRevisionId: uuid(data.draftRevisionId) };
}

/**
 * A26/A34: a frozen legacy design has NO thumbnail, every other listed
 * template has one. A row that says otherwise is not a catalog row.
 */
function thumbnailAssetKey(
  kind: StoreWebsiteEditorTemplate['kind'],
  value: unknown,
): string | null {
  if (kind === 'legacy-frozen') return value === null || value === undefined ? null : invalid();
  return typeof value === 'string' && WEBSITE_TEMPLATE_ASSET_KEY_RE.test(value) ? value : invalid();
}

function parseCatalogItem(value: unknown, supabaseUrl: string): WebsiteTemplateCatalogItem {
  if (!isRecord(value)) return invalid();
  if (!isOneOf(WEBSITE_TEMPLATE_KINDS, value.kind)) return invalid();
  const thumbnailKey = thumbnailAssetKey(value.kind, value.thumbnailAssetKey);
  const thumbnailSrc =
    thumbnailKey === null
      ? null
      : websiteMediaUrl(`${WEBSITE_TEMPLATE_ASSET_REF_PREFIX}${thumbnailKey}`, supabaseUrl);
  if (thumbnailKey !== null && !thumbnailSrc) return invalid();
  if (!isOneOf(WEBSITE_TEMPLATE_CATEGORIES, value.category)) return invalid();
  if (typeof value.slug !== 'string' || !SLUG_RE.test(value.slug)) return invalid();
  if (
    !Array.isArray(value.styleTags) ||
    !value.styleTags.every((tag): tag is string => typeof tag === 'string' && SLUG_RE.test(tag))
  ) {
    return invalid();
  }
  if (
    !Array.isArray(value.swatches) ||
    value.swatches.length > 4 ||
    !value.swatches.every(isWebsiteHexColor)
  ) {
    return invalid();
  }
  return {
    templateId: uuid(value.templateId),
    slug: value.slug,
    currentVersionId: uuid(value.currentVersionId),
    kind: value.kind,
    category: value.category,
    styleTags: value.styleTags,
    titleKey: i18nKey(value.titleKey),
    descriptionKey: i18nKey(value.descriptionKey),
    thumbnailSrc,
    swatches: value.swatches,
    isCurrent: value.isCurrent === true,
    isInDraft: value.isInDraft === true,
  };
}

function parseReadiness(value: unknown): WebsiteReadinessIssue[] {
  if (!Array.isArray(value)) return invalid();
  return value.map((raw) => {
    if (!isRecord(raw) || !isOneOf(WEBSITE_ISSUE_CODES, raw.code)) return invalid();
    // Epic #228 P1: a server without severity meant "blocks", as it always did.
    const severity =
      raw.severity === undefined || raw.severity === null
        ? 'blocking'
        : isOneOf(WEBSITE_READINESS_SEVERITIES, raw.severity)
          ? raw.severity
          : invalid();
    return {
      code: raw.code,
      severity,
      ...(raw.slotId === undefined || raw.slotId === null ? {} : { slotId: slotId(raw.slotId) }),
      ...(raw.pageKind === undefined || raw.pageKind === null
        ? {}
        : { pageKind: isWebsitePageKind(raw.pageKind) ? raw.pageKind : invalid() }),
    };
  });
}

/**
 * Render data stays in wire form: media is `media:<uuid>` / `template:<key>`.
 * Admin forwards it to the storefront canvas, which resolves the references
 * against its own Supabase origin and never loads a URL it was handed.
 */
function parseRenderDto(value: unknown): PublicStoreWebsiteDto {
  const parsed = parsePublicStoreWebsiteDto(value);
  if (!parsed.ok) {
    throw new WebsiteTemplateServiceError(
      parsed.reason === 'unsupported' ? 'template_unavailable' : 'unknown',
    );
  }
  if (parsed.dto.mode !== 'template') return invalid();
  return parsed.dto;
}

export interface ListWebsiteCatalogInput {
  readonly storeId: string;
  readonly category?: WebsiteTemplateCategory | null;
  readonly style?: string | null;
  readonly cursor?: string | null;
  readonly limit?: number;
  readonly supabaseUrl: string;
}

/**
 * Keyset page of library metadata: the platform catalog, the store's own
 * published custom templates and, on the first unfiltered page only, the
 * store's own frozen legacy design. Never returns manifests.
 */
export async function listCatalog(
  client: SupabaseClient,
  input: ListWebsiteCatalogInput,
): Promise<WebsiteTemplateCatalogPage> {
  const limit = Math.min(
    Math.max(Math.trunc(input.limit ?? WEBSITE_CATALOG_PAGE_LIMIT), 1),
    WEBSITE_CATALOG_PAGE_LIMIT,
  );
  const data = await callWebsiteRpc(client, 'list_website_template_catalog_v2', {
    p_client_contract: WEBSITE_CLIENT_CONTRACT,
    p_store_id: input.storeId,
    p_category: input.category ?? null,
    p_style: input.style ?? null,
    p_cursor: input.cursor ?? null,
    p_limit: limit,
  });
  if (!isRecord(data) || !Array.isArray(data.items)) return invalid();
  return {
    items: data.items.map((item) => parseCatalogItem(item, input.supabaseUrl)),
    nextCursor: cursor(data.nextCursor),
  };
}

export async function getEditorState(
  client: SupabaseClient,
  storeId: string,
): Promise<StoreWebsiteEditorState> {
  const data = await callWebsiteRpc(client, 'get_store_website_editor_state', {
    p_store_id: storeId,
  });
  if (!isRecord(data)) return invalid();
  if (data.mode !== 'template' && data.mode !== 'legacy') return invalid();
  if (!isOneOf(['none', 'converted', 'unmappable'] as const, data.conversion)) return invalid();

  let manifest: StoreWebsiteEditorState['manifest'] = null;
  if (data.manifest !== null && data.manifest !== undefined) {
    const parsed = parseWebsiteTemplateManifest(data.manifest);
    // A manifest this Admin build cannot parse needs newer code, not a guess.
    if (!parsed.ok) throw new WebsiteTemplateServiceError('template_unavailable');
    manifest = parsed.manifest;
  }

  let draft: StoreWebsiteEditorState['draft'] = null;
  if (data.draft !== null && data.draft !== undefined) {
    if (!isRecord(data.draft) || !manifest) return invalid();
    const document = parseStoreWebsiteDocument(manifest, data.draft.document);
    if (!document.ok) return invalid();
    draft = {
      revisionId: uuid(data.draft.revisionId),
      document: document.document,
      updatedAt: text(data.draft.updatedAt),
    };
  }

  let live: StoreWebsiteEditorState['live'] = null;
  if (data.live !== null && data.live !== undefined) {
    if (!isRecord(data.live)) return invalid();
    live = {
      revisionId: uuid(data.live.revisionId),
      templateVersionId: uuid(data.live.templateVersionId),
      publishedAt: text(data.live.publishedAt),
    };
  }

  // C7: the draft's template is there exactly when there is a draft.
  let template: StoreWebsiteEditorTemplate | null = null;
  if (data.template !== null && data.template !== undefined) {
    if (!draft) return invalid();
    template = parseEditorTemplate(data.template);
  } else if (draft) {
    return invalid();
  }

  const liveTemplateId =
    data.liveTemplateId === null || data.liveTemplateId === undefined
      ? null
      : uuid(data.liveTemplateId);

  return {
    mode: data.mode,
    conversion: data.conversion,
    live,
    draft,
    manifest,
    templateTitleKey:
      data.templateTitleKey === null || data.templateTitleKey === undefined
        ? null
        : i18nKey(data.templateTitleKey),
    readiness: parseReadiness(data.readiness),
    hasUnpublishedChanges: data.hasUnpublishedChanges === true,
    template,
    liveTemplateId,
  };
}

function parseEditorTemplate(value: unknown): StoreWebsiteEditorTemplate {
  if (!isRecord(value)) return invalid();
  if (!isOneOf(WEBSITE_TEMPLATE_KINDS, value.kind)) return invalid();
  if (!isOneOf(WEBSITE_TEMPLATE_CATEGORIES, value.category)) return invalid();
  if (typeof value.slug !== 'string' || !SLUG_RE.test(value.slug)) return invalid();
  return {
    templateId: uuid(value.templateId),
    slug: value.slug,
    kind: value.kind,
    category: value.category,
    titleKey: i18nKey(value.titleKey),
    thumbnailAssetKey: thumbnailAssetKey(value.kind, value.thumbnailAssetKey),
  };
}

/** A16: render-only preview of an unassigned template. Persists nothing. */
export async function getTemplatePreviewRenderData(
  client: SupabaseClient,
  input: {
    readonly storeId: string;
    readonly templateVersionId: string;
    readonly pageKind: WebsitePageKind;
  },
): Promise<PublicStoreWebsiteDto> {
  const data = await callWebsiteRpc(client, 'get_website_template_preview_render_data_v2', {
    p_client_contract: WEBSITE_CLIENT_CONTRACT,
    p_store_id: input.storeId,
    p_template_version_id: input.templateVersionId,
    p_page_kind: input.pageKind,
  });
  return parseRenderDto(data);
}

export async function getCanvasRenderData(
  client: SupabaseClient,
  input: {
    readonly storeId: string;
    readonly pageKind: WebsitePageKind;
  },
): Promise<WebsiteCanvasRenderData> {
  return parseCanvasRenderData(
    await callWebsiteRpc(client, 'resolve_store_website_canvas_render_data', {
      p_store_id: input.storeId,
      p_page_kind: input.pageKind,
    }),
  );
}

function parseCanvasRenderData(data: unknown): WebsiteCanvasRenderData {
  if (!isRecord(data) || !Array.isArray(data.slotChrome)) return invalid();
  const slotChrome: WebsiteSlotChrome[] = data.slotChrome.map((raw) => {
    if (!isRecord(raw)) return invalid();
    return { slotId: slotId(raw.slotId), labelKey: i18nKey(raw.labelKey), ...ordinal(raw.ordinal) };
  });
  return { dto: parseRenderDto(data.dto), slotChrome };
}

interface DraftWriteInput {
  readonly storeId: string;
  /** CAS: null only when the store has no draft yet. */
  readonly expectedDraftRevisionId: string | null;
  /** Idempotency key; reuse it when retrying the same user action. */
  readonly clientMutationId: string;
}

interface CasArgs {
  readonly p_store_id: string;
  readonly p_expected_draft_revision_id: string | null;
  readonly p_client_mutation_id: string;
}

const casArgs = (input: DraftWriteInput): CasArgs => ({
  p_store_id: input.storeId,
  p_expected_draft_revision_id: input.expectedDraftRevisionId,
  p_client_mutation_id: input.clientMutationId,
});

export async function stageTemplate(
  client: SupabaseClient,
  input: DraftWriteInput & { readonly templateVersionId: string },
): Promise<WebsiteStageResult> {
  const data = await callWebsiteRpc(client, 'stage_store_website_template_v2', {
    p_client_contract: WEBSITE_CLIENT_CONTRACT,
    ...casArgs(input),
    p_template_version_id: input.templateVersionId,
  });
  if (!isRecord(data) || !Array.isArray(data.carriedOver) || !Array.isArray(data.notCarriedOver)) {
    return invalid();
  }
  return {
    draftRevisionId: uuid(data.draftRevisionId),
    carriedOver: data.carriedOver.map(slotId),
    notCarriedOver: data.notCarriedOver.map(parseNotCarriedSlot),
  };
}

function parseNotCarriedSlot(raw: unknown): WebsiteNotCarriedSlot {
  if (!isRecord(raw) || !isOneOf(WEBSITE_SLOT_SEMANTICS, raw.semantic)) return invalid();
  return { slotId: slotId(raw.slotId), semantic: raw.semantic, ...ordinal(raw.ordinal) };
}

/**
 * `value: null` resets the slot to the template default.
 *
 * @deprecated A21: Admin saves a region with `saveSlots`, one revision per
 * dialog. Kept for scripts and tests.
 */
export async function saveSlot(
  client: SupabaseClient,
  input: DraftWriteInput & {
    readonly slotId: string;
    readonly value: WebsiteSlotValue | null;
  },
): Promise<WebsiteDraftMutationResult> {
  return draftResult(
    await callWebsiteRpc(client, 'save_store_website_slot', {
      ...casArgs(input),
      p_slot_id: input.slotId,
      p_value: input.value,
    }),
  );
}

/** A batch that cannot be a batch is refused before any request. */
function assertSlotBatch(input: WebsiteSlotBatch): Record<string, unknown> {
  const values: unknown = input;
  if (!isRecord(values)) throw new WebsiteTemplateServiceError('invalid_slot');
  const entries = Object.entries(values);
  if (entries.length === 0 || entries.length > WEBSITE_SLOT_BATCH_MAX) {
    throw new WebsiteTemplateServiceError('invalid_slot');
  }
  for (const [, value] of entries) {
    if (value !== null && !isRecord(value)) throw new WebsiteTemplateServiceError('invalid_slot');
  }
  return values;
}

export interface SaveWebsiteSlotsInput extends DraftWriteInput {
  /** 1..12 slots of ONE region. `null` = use the template default again. */
  readonly values: WebsiteSlotBatch;
}

export interface WebsiteSlotsSaveResult extends WebsiteDraftMutationResult {
  /** Manifest order. */
  readonly savedSlotIds: readonly string[];
}

/**
 * C4 atomic batch save: one revision, one CAS check, one idempotency key.
 * Either every slot of the batch is saved or nothing is.
 *
 * A batch that cannot be a batch (empty, over 12 slots, not an object, a
 * value that is neither an object nor null) is refused HERE, before any
 * request: an empty Save must never reach the server.
 */
export async function saveSlots(
  client: SupabaseClient,
  input: SaveWebsiteSlotsInput,
): Promise<WebsiteSlotsSaveResult> {
  const values = assertSlotBatch(input.values);
  const data = await callWebsiteRpc(client, 'save_store_website_slots', {
    ...casArgs(input),
    p_values: values,
  });
  if (!isRecord(data) || !Array.isArray(data.savedSlotIds)) return invalid();
  return {
    draftRevisionId: uuid(data.draftRevisionId),
    savedSlotIds: data.savedSlotIds.map(slotId),
  };
}

export async function setInterfaceLocale(
  client: SupabaseClient,
  input: DraftWriteInput & { readonly locale: WebsiteInterfaceLocale },
): Promise<WebsiteDraftMutationResult> {
  if (!isOneOf(WEBSITE_INTERFACE_LOCALES, input.locale)) return invalid();
  return draftResult(
    await callWebsiteRpc(client, 'set_store_website_interface_locale', {
      ...casArgs(input),
      p_locale: input.locale,
    }),
  );
}

export async function getReadiness(
  client: SupabaseClient,
  storeId: string,
): Promise<WebsiteReadinessIssue[]> {
  return parseReadiness(
    await callWebsiteRpc(client, 'validate_store_website_publish_readiness', {
      p_store_id: storeId,
    }),
  );
}

export async function publish(
  client: SupabaseClient,
  input: DraftWriteInput & { readonly expectedDraftRevisionId: string },
): Promise<WebsitePublishResult> {
  const data = await callWebsiteRpc(client, 'publish_store_website_v2', {
    ...casArgs(input),
    p_client_contract: WEBSITE_CLIENT_CONTRACT,
  });
  if (!isRecord(data)) return invalid();
  return { liveRevisionId: uuid(data.liveRevisionId) };
}

/** A25: ops and support only. No Admin UI lists or restores revisions. */
export async function listRevisions(
  client: SupabaseClient,
  input: { readonly storeId: string; readonly cursor?: string | null; readonly limit?: number },
): Promise<StoreWebsiteRevisionPage> {
  const data = await callWebsiteRpc(client, 'list_store_website_revisions', {
    p_store_id: input.storeId,
    p_cursor: input.cursor ?? null,
    p_limit: Math.min(Math.max(Math.trunc(input.limit ?? 20), 1), 50),
  });
  if (!isRecord(data) || !Array.isArray(data.items)) return invalid();
  const items: StoreWebsiteRevisionSummary[] = data.items.map((raw) => {
    if (!isRecord(raw)) return invalid();
    if (!isOneOf(REVISION_STATUSES, raw.status) || !isOneOf(REVISION_SOURCES, raw.source)) {
      return invalid();
    }
    if (!Number.isInteger(raw.revisionNumber)) return invalid();
    return {
      revisionId: uuid(raw.revisionId),
      revisionNumber: raw.revisionNumber as number,
      status: raw.status,
      source: raw.source,
      templateTitleKey:
        raw.templateTitleKey === null || raw.templateTitleKey === undefined
          ? null
          : i18nKey(raw.templateTitleKey),
      createdAt: text(raw.createdAt),
      publishedAt:
        raw.publishedAt === null || raw.publishedAt === undefined ? null : text(raw.publishedAt),
    };
  });
  return { items, nextCursor: cursor(data.nextCursor) };
}

/** A25: ops and support only. No Admin UI lists or restores revisions. */
export async function restoreToDraft(
  client: SupabaseClient,
  input: DraftWriteInput & { readonly revisionId: string },
): Promise<WebsiteDraftMutationResult> {
  return draftResult(
    await callWebsiteRpc(client, 'restore_store_website_revision_to_draft_v2', {
      p_client_contract: WEBSITE_CLIENT_CONTRACT,
      ...casArgs(input),
      p_revision_id: input.revisionId,
    }),
  );
}

export interface WebsiteDraftBrowseGrant {
  /** 64 lowercase hex characters. Memory only: never store or log it. */
  readonly token: string;
  /** ISO 8601 instant, at most 30 minutes ahead. */
  readonly expiresAt: string;
}

/**
 * C14: mints a draft-browse grant for the admin's OWN store and session. The
 * canvas host owns the lifecycle (mint at session start, replace before it
 * expires, revoke on unmount), so there is no React Query hook for it.
 */
export async function createDraftBrowseGrant(
  client: SupabaseClient,
  storeId: string,
): Promise<WebsiteDraftBrowseGrant> {
  const data = await callWebsiteRpc(client, 'create_store_website_draft_browse_grant', {
    p_store_id: storeId,
  });
  return parseWebsiteCanvasDraftBrowseGrant(data) ?? invalid();
}

/** Revokes one of the caller's own grants. Revoking twice is not an error. */
export async function revokeDraftBrowseGrant(
  client: SupabaseClient,
  input: { readonly storeId: string; readonly token: string },
): Promise<void> {
  // A value that cannot be a token is never sent anywhere.
  if (!isWebsiteDraftBrowseToken(input.token)) return invalid();
  await callWebsiteRpc(client, 'revoke_store_website_draft_browse_grant', {
    p_store_id: input.storeId,
    p_token: input.token,
  });
}

export async function getConversionReport(
  client: SupabaseClient,
  storeId: string,
): Promise<StoreWebsiteConversionReport | null> {
  const data = await callWebsiteRpc(client, 'get_store_website_conversion_report', {
    p_store_id: storeId,
  });
  if (data === null || data === undefined) return null;
  if (!isRecord(data)) return invalid();
  if (data.result !== 'converted' && data.result !== 'unmappable') return invalid();
  if (!isOneOf(WEBSITE_INTERFACE_LOCALES, data.sourceLocale)) return invalid();
  const count = (value: unknown): number =>
    Number.isInteger(value) && (value as number) >= 0 ? (value as number) : invalid();
  if (
    !Array.isArray(data.frozenItemKeys) ||
    !data.frozenItemKeys.every(
      (key): key is string => typeof key === 'string' && /^[A-Za-z0-9.-]{1,80}$/.test(key),
    )
  ) {
    return invalid();
  }
  return {
    result: data.result,
    sourceLocale: data.sourceLocale,
    mappedSlots: count(data.mappedSlots),
    frozenItems: count(data.frozenItems),
    frozenItemKeys: data.frozenItemKeys,
    discardedLocaleVariants: count(data.discardedLocaleVariants),
  };
}

// ---------------------------------------------------------------------------
// Epic #228 P3: per-template drafts (v3). Every call names its template and
// sends WEBSITE_CLIENT_CONTRACT. Every parser checks the exact key sets of
// `lib/website-templates/__fixtures__/template-drafts-v3-contract.json`:
// - a missing or extra key, or an unknown enum value, means the server speaks
//   a contract this build does not know, so it fails closed with
//   `template_unavailable` (like `parseRenderDto`);
// - a malformed value or a response that contradicts itself is `unknown`, as
//   everywhere else in this file.
// Nothing here reads the store's compatibility draft pointer: what is shown
// and saved is always the named template's head.

const EDITOR_STATE_V3_KEYS = [
  'mode',
  'conversion',
  'live',
  'draft',
  'manifest',
  'templateTitleKey',
  'readiness',
  'hasUnpublishedChanges',
  'template',
  'liveTemplateId',
  'templateId',
  'head',
  'publicStatus',
  'updateAvailable',
  'templates',
] as const;

const CATALOG_ITEM_V2_KEYS = [
  'templateId',
  'slug',
  'currentVersionId',
  'kind',
  'category',
  'styleTags',
  'titleKey',
  'descriptionKey',
  'thumbnailAssetKey',
  'swatches',
  'isCurrent',
  'isInDraft',
] as const;

/** Exact wire key sets of every v3 return; equal to the contract fixture (tested). */
export const WEBSITE_V3_SHAPE_KEYS = {
  editorState: EDITOR_STATE_V3_KEYS,
  'editorState.head': ['revisionId', 'contentHash'],
  'editorState.live': [
    'revisionId',
    'templateId',
    'templateVersionId',
    'publishedAt',
    'contentHash',
  ],
  'editorState.draft': ['revisionId', 'document', 'updatedAt'],
  'editorState.template': [
    'templateId',
    'slug',
    'kind',
    'category',
    'titleKey',
    'thumbnailAssetKey',
  ],
  readinessReport: ['blocking', 'warnings'],
  templateHead: [
    'templateId',
    'headRevisionId',
    'templateVersionId',
    'currentVersionId',
    'contentHash',
    'updatedAt',
    'publicStatus',
    'hasUnpublishedChanges',
  ],
  catalogPage: ['items', 'nextCursor'],
  catalogItem: [...CATALOG_ITEM_V2_KEYS, 'isLive', 'isSelected', 'draft'],
  'catalogItem.draft': ['headRevisionId', 'updatedAt', 'hasUnpublishedChanges'],
  slotsResult: ['draftRevisionId', 'contentHash'],
  publishResult: ['liveRevisionId', 'contentHash'],
  updateResult: [...EDITOR_STATE_V3_KEYS, 'carriedOver', 'notCarriedOver'],
  canvas: ['dto', 'slotChrome'],
} as const satisfies Readonly<Record<string, readonly string[]>>;

export type WebsiteV3Shape = keyof typeof WEBSITE_V3_SHAPE_KEYS;

const CONTENT_HASH_RE = /^[0-9a-f]{64}$/;
const EDITOR_MODES = ['template', 'legacy'] as const;
const EDITOR_CONVERSIONS = ['none', 'converted', 'unmappable'] as const;

/** The server's shape is not one this build knows: it needs newer code, not a guess. */
function drift(): never {
  throw new WebsiteTemplateServiceError('template_unavailable');
}

function exact(value: unknown, shape: WebsiteV3Shape): Record<string, unknown> {
  if (!isRecord(value)) return invalid();
  const keys: readonly string[] = WEBSITE_V3_SHAPE_KEYS[shape];
  const actual = Object.keys(value);
  if (
    actual.length !== keys.length ||
    !keys.every((key) => Object.prototype.hasOwnProperty.call(value, key))
  ) {
    return drift();
  }
  return value;
}

function bool(value: unknown): boolean {
  return typeof value === 'boolean' ? value : invalid();
}

function contentHash(value: unknown): string | null {
  if (value === null) return null;
  return typeof value === 'string' && CONTENT_HASH_RE.test(value) ? value : invalid();
}

function nullable<T>(value: unknown, parse: (raw: unknown) => T): T | null {
  return value === null ? null : parse(value);
}

function publicStatus(value: unknown): TemplatePublicStatus {
  return isOneOf(TEMPLATE_PUBLIC_STATUSES, value) ? value : drift();
}

/** A value that cannot be a template id never reaches the server. */
function templateIdArg(templateId: string): string {
  if (!WEBSITE_UUID_RE.test(templateId)) {
    throw new WebsiteTemplateServiceError('template_unavailable');
  }
  return templateId;
}

function parseTemplateHead(value: unknown): StoreWebsiteTemplateHead {
  const raw = exact(value, 'templateHead');
  const status = publicStatus(raw.publicStatus);
  const hasUnpublishedChanges = bool(raw.hasUnpublishedChanges);
  if (hasUnpublishedChanges && status !== 'live') return invalid();
  return {
    templateId: uuid(raw.templateId),
    headRevisionId: uuid(raw.headRevisionId),
    templateVersionId: uuid(raw.templateVersionId),
    currentVersionId: uuid(raw.currentVersionId),
    contentHash: contentHash(raw.contentHash),
    updatedAt: text(raw.updatedAt),
    publicStatus: status,
    hasUnpublishedChanges,
  };
}

function parseReadinessReport(value: unknown): WebsiteReadinessReport {
  const raw = exact(value, 'readinessReport');
  const blocking = parseReadiness(raw.blocking);
  const warnings = parseReadiness(raw.warnings);
  if (
    !blocking.every((issue) => issue.severity === 'blocking') ||
    !warnings.every((issue) => issue.severity === 'warning')
  ) {
    return invalid();
  }
  return { blocking, warnings };
}

function parseEditorStateV3Fields(data: Record<string, unknown>): StoreWebsiteEditorStateV3 {
  if (!isOneOf(EDITOR_MODES, data.mode)) return drift();
  if (!isOneOf(EDITOR_CONVERSIONS, data.conversion)) return drift();

  let manifest: StoreWebsiteEditorStateV3['manifest'] = null;
  if (data.manifest !== null) {
    const parsed = parseWebsiteTemplateManifest(data.manifest);
    if (!parsed.ok) return drift();
    manifest = parsed.manifest;
  }

  const head = nullable(data.head, (value) => {
    const raw = exact(value, 'editorState.head');
    return { revisionId: uuid(raw.revisionId), contentHash: contentHash(raw.contentHash) };
  });

  let draft: StoreWebsiteEditorStateV3['draft'] = null;
  if (data.draft !== null) {
    const raw = exact(data.draft, 'editorState.draft');
    if (!manifest) return invalid();
    const document = parseStoreWebsiteDocument(manifest, raw.document);
    if (!document.ok) return invalid();
    draft = {
      revisionId: uuid(raw.revisionId),
      document: document.document,
      updatedAt: text(raw.updatedAt),
    };
  }

  const live = nullable(data.live, (value) => {
    const raw = exact(value, 'editorState.live');
    return {
      revisionId: uuid(raw.revisionId),
      templateId: uuid(raw.templateId),
      templateVersionId: uuid(raw.templateVersionId),
      publishedAt: text(raw.publishedAt),
      contentHash: contentHash(raw.contentHash),
    };
  });

  if (!Array.isArray(data.templates)) return invalid();

  const state: StoreWebsiteEditorStateV3 = {
    mode: data.mode,
    conversion: data.conversion,
    live,
    draft,
    manifest,
    templateTitleKey: nullable(data.templateTitleKey, i18nKey),
    readiness: parseReadinessReport(data.readiness),
    hasUnpublishedChanges: bool(data.hasUnpublishedChanges),
    template: nullable(data.template, (value) =>
      parseEditorTemplate(exact(value, 'editorState.template')),
    ),
    liveTemplateId: nullable(data.liveTemplateId, uuid),
    templateId: nullable(data.templateId, uuid),
    head,
    publicStatus: nullable(data.publicStatus, publicStatus),
    updateAvailable: bool(data.updateAvailable),
    templates: data.templates.map(parseTemplateHead),
  };

  // The contract's invariants (fixture `invariants`): a response that breaks
  // one is not an editor state, whatever its keys.
  if ((state.draft === null) !== (state.head === null)) return invalid();
  if (state.draft && state.head && state.draft.revisionId !== state.head.revisionId) {
    return invalid();
  }
  if ((state.template === null) !== (state.draft === null)) return invalid();
  if (state.template && state.template.templateId !== state.templateId) return invalid();
  if (state.head !== null && state.templateId === null) return invalid();
  if ((state.live?.templateId ?? null) !== state.liveTemplateId) return invalid();
  if (state.publicStatus !== deriveTemplatePublicStatus(state)) return invalid();
  if (state.hasUnpublishedChanges && state.publicStatus !== 'live') return invalid();
  if (state.updateAvailable && state.head === null) return invalid();
  return state;
}

function parseEditorStateV3(value: unknown): StoreWebsiteEditorStateV3 {
  return parseEditorStateV3Fields(exact(value, 'editorState'));
}

function parseCatalogItemV3(value: unknown, supabaseUrl: string): WebsiteTemplateCatalogItemV3 {
  const raw = exact(value, 'catalogItem');
  const isLive = bool(raw.isLive);
  const draft = nullable(raw.draft, (entry) => {
    const draftRaw = exact(entry, 'catalogItem.draft');
    return {
      headRevisionId: uuid(draftRaw.headRevisionId),
      updatedAt: text(draftRaw.updatedAt),
      hasUnpublishedChanges: bool(draftRaw.hasUnpublishedChanges),
    };
  });
  if (draft?.hasUnpublishedChanges === true && !isLive) return invalid();
  return {
    ...parseCatalogItem(raw, supabaseUrl),
    isCurrent: bool(raw.isCurrent),
    isInDraft: bool(raw.isInDraft),
    isLive,
    isSelected: bool(raw.isSelected),
    draft,
  };
}

function parseSlotsResultV3(value: unknown): SaveSlotsV3Result {
  const raw = exact(value, 'slotsResult');
  return { draftRevisionId: uuid(raw.draftRevisionId), contentHash: contentHash(raw.contentHash) };
}

export interface TemplateDraftInput {
  readonly storeId: string;
  readonly templateId: string;
}

export interface TemplateDraftWriteInput extends TemplateDraftInput {
  /** Idempotency key; reuse it when retrying the same user action. */
  readonly clientMutationId: string;
}

export interface TemplateHeadWriteInput extends TemplateDraftWriteInput {
  /** CAS on this template's head. */
  readonly expectedHeadRevisionId: string;
}

/**
 * Opens a template's draft: the existing head, a copy of an archived one, or
 * the template's defaults on first use. Never publishes and never touches
 * another template's draft.
 */
export async function openTemplateDraft(
  client: SupabaseClient,
  input: TemplateDraftWriteInput,
): Promise<StoreWebsiteEditorStateV3> {
  return parseEditorStateV3(
    await callWebsiteRpc(client, 'open_store_website_template_draft_v3', {
      p_store_id: input.storeId,
      p_template_id: templateIdArg(input.templateId),
      p_client_mutation_id: input.clientMutationId,
      p_client_contract: WEBSITE_CLIENT_CONTRACT,
    }),
  );
}

/**
 * The editor state of one template. `templateId: null` is the entry-route
 * read only: it describes the template the store last opened.
 */
export async function getEditorStateV3(
  client: SupabaseClient,
  storeId: string,
  templateId: string | null,
): Promise<StoreWebsiteEditorStateV3> {
  return parseEditorStateV3(
    await callWebsiteRpc(client, 'get_store_website_editor_state_v3', {
      p_store_id: storeId,
      p_template_id: templateId === null ? null : templateIdArg(templateId),
      p_client_contract: WEBSITE_CLIENT_CONTRACT,
    }),
  );
}

/** Same render data as v1/v2, for the named template's head. */
export async function getCanvasRenderDataV3(
  client: SupabaseClient,
  input: TemplateDraftInput & { readonly pageKind: WebsitePageKind },
): Promise<WebsiteCanvasRenderData> {
  const data = await callWebsiteRpc(client, 'resolve_store_website_canvas_render_data_v3', {
    p_store_id: input.storeId,
    p_template_id: templateIdArg(input.templateId),
    p_page_kind: input.pageKind,
    p_client_contract: WEBSITE_CLIENT_CONTRACT,
  });
  return parseCanvasRenderData(exact(data, 'canvas'));
}

export interface SaveWebsiteSlotsV3Input extends TemplateDraftWriteInput {
  /** CAS on this template's head; null only when the template has no head yet. */
  readonly expectedHeadRevisionId: string | null;
  /** 1..12 slots of ONE region. `null` = use the template default again. */
  readonly values: WebsiteSlotBatch;
}

/** One region's atomic save into the named template's head (same batch rules as `saveSlots`). */
export async function saveSlotsV3(
  client: SupabaseClient,
  input: SaveWebsiteSlotsV3Input,
): Promise<SaveSlotsV3Result> {
  const values = assertSlotBatch(input.values);
  return parseSlotsResultV3(
    await callWebsiteRpc(client, 'save_store_website_slots_v3', {
      p_store_id: input.storeId,
      p_template_id: templateIdArg(input.templateId),
      p_values: values,
      p_expected_head_revision_id: input.expectedHeadRevisionId,
      p_client_mutation_id: input.clientMutationId,
      p_client_contract: WEBSITE_CLIENT_CONTRACT,
    }),
  );
}

export async function setInterfaceLocaleV3(
  client: SupabaseClient,
  input: TemplateHeadWriteInput & { readonly locale: WebsiteInterfaceLocale },
): Promise<SaveSlotsV3Result> {
  if (!isOneOf(WEBSITE_INTERFACE_LOCALES, input.locale)) return invalid();
  return parseSlotsResultV3(
    await callWebsiteRpc(client, 'set_store_website_interface_locale_v3', {
      p_store_id: input.storeId,
      p_template_id: templateIdArg(input.templateId),
      p_locale: input.locale,
      p_expected_head_revision_id: input.expectedHeadRevisionId,
      p_client_mutation_id: input.clientMutationId,
      p_client_contract: WEBSITE_CLIENT_CONTRACT,
    }),
  );
}

export async function getReadinessV3(
  client: SupabaseClient,
  storeId: string,
  templateId: string,
): Promise<WebsiteReadinessReport> {
  return parseReadinessReport(
    await callWebsiteRpc(client, 'validate_store_website_publish_readiness_v3', {
      p_store_id: storeId,
      p_template_id: templateIdArg(templateId),
      p_client_contract: WEBSITE_CLIENT_CONTRACT,
    }),
  );
}

/** Makes the named template's head live. Other templates' drafts are untouched. */
export async function publishV3(
  client: SupabaseClient,
  input: TemplateHeadWriteInput,
): Promise<PublishV3Result> {
  const raw = exact(
    await callWebsiteRpc(client, 'publish_store_website_v3', {
      p_store_id: input.storeId,
      p_template_id: templateIdArg(input.templateId),
      p_expected_head_revision_id: input.expectedHeadRevisionId,
      p_client_mutation_id: input.clientMutationId,
      p_client_contract: WEBSITE_CLIENT_CONTRACT,
    }),
    'publishResult',
  );
  return { liveRevisionId: uuid(raw.liveRevisionId), contentHash: contentHash(raw.contentHash) };
}

/**
 * Moves the named template's head to the version the store may use now and
 * reports which values moved. A head already on that version comes back
 * unchanged with empty lists.
 */
export async function updateTemplateVersionV3(
  client: SupabaseClient,
  input: TemplateHeadWriteInput,
): Promise<UpdateTemplateVersionV3Result> {
  const raw = exact(
    await callWebsiteRpc(client, 'update_store_website_template_version_v3', {
      p_store_id: input.storeId,
      p_template_id: templateIdArg(input.templateId),
      p_expected_head_revision_id: input.expectedHeadRevisionId,
      p_client_mutation_id: input.clientMutationId,
      p_client_contract: WEBSITE_CLIENT_CONTRACT,
    }),
    'updateResult',
  );
  if (!Array.isArray(raw.carriedOver) || !Array.isArray(raw.notCarriedOver)) return invalid();
  return {
    ...parseEditorStateV3Fields(raw),
    carriedOver: raw.carriedOver.map(slotId),
    notCarriedOver: raw.notCarriedOver.map(parseNotCarriedSlot),
  };
}

/** `listCatalog` plus each card's live flag and saved draft. */
export async function listCatalogV3(
  client: SupabaseClient,
  input: ListWebsiteCatalogInput,
): Promise<WebsiteTemplateCatalogPageV3> {
  const limit = Math.min(
    Math.max(Math.trunc(input.limit ?? WEBSITE_CATALOG_PAGE_LIMIT), 1),
    WEBSITE_CATALOG_PAGE_LIMIT,
  );
  const raw = exact(
    await callWebsiteRpc(client, 'list_website_template_catalog_v3', {
      p_client_contract: WEBSITE_CLIENT_CONTRACT,
      p_store_id: input.storeId,
      p_category: input.category ?? null,
      p_style: input.style ?? null,
      p_cursor: input.cursor ?? null,
      p_limit: limit,
    }),
    'catalogPage',
  );
  if (!Array.isArray(raw.items)) return invalid();
  return {
    items: raw.items.map((item) => parseCatalogItemV3(item, input.supabaseUrl)),
    nextCursor: cursor(raw.nextCursor),
  };
}

export const websiteTemplatesAdminService = {
  listCatalog,
  getEditorState,
  getTemplatePreviewRenderData,
  getCanvasRenderData,
  stageTemplate,
  saveSlot,
  saveSlots,
  setInterfaceLocale,
  getReadiness,
  publish,
  listRevisions,
  restoreToDraft,
  createDraftBrowseGrant,
  revokeDraftBrowseGrant,
  getConversionReport,
} as const;
